import { DeleteOutlined } from '@ant-design/icons'
import { Button, Form } from 'antd'
import { AxiosError } from 'axios'
import { t } from 'i18next'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import {
  BackButton,
  ConfirmDeleteModal,
  DetailsPageLayout,
  HeaderSeparator,
  PageContentDropdown,
  SectionLoadingState,
  Spacer,
  SubHeader,
} from '../../components'
import {
  useMediaQuery,
  usePermissions,
  useSearchReviewDetails,
} from '../../hooks'
import { notificationService } from '../../services'
import { SearchReview } from '../../store/reducers/searchReviewReducer'
import { routePaths } from '../RootPage'
import SelectWorkspacePanelType from '../WorkspaceDetailsPage/components/SelectWorkspacePanelType'

import { SaveOutlined } from '@ant-design/icons'
import {
  SearchReviewDetailsOverviewInfoPanel,
  SearchReviewDetailsOverviewQueriesPanel,
} from './panels'
import SearchReviewTabs from './SearchReviewTabs'

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
`

interface FormValues {
  title: string
  description?: string
}

export type SearchReviewPanelType = 'overview' // The use of camel case is to have camel case in the url
export type SearchReviewPanelTypes = {
  OVERVIEW: SearchReviewPanelType
}

export const searchReviewPanelTypes: SearchReviewPanelTypes = {
  OVERVIEW: 'overview',
}

export type SearchReviewTabType = 'info' | 'queries' // The use of camel case is to have camel case in the url
export type SearchReviewTabTypes = {
  INFO: SearchReviewTabType
  QUERIES: SearchReviewTabType
}

export const searchReviewTabs: SearchReviewTabTypes = {
  INFO: 'info',
  QUERIES: 'queries',
}

const SearchReviewDetailsPage = () => {
  const [form] = Form.useForm()
  const navigate = useNavigate()

  const { searchReviewId, panelType, tab } = useParams()
  const isMobile = useMediaQuery('xs')
  const {
    selectedSearchReview,
    exportSearchReviewCsv,
    updateSearchReviewDetails,
    searchReviewLoading,
    deleteSearchReview,
    refreshSearchReviewDetails,
  } = useSearchReviewDetails({
    searchReviewId: searchReviewId || '',
  })

  const { isDeletable, isUpdatable } = usePermissions({
    uiFlag: selectedSearchReview?.uiFlags || 0,
  })

  const [saveLoading, setSaveLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false)

  const isSearchReviewLoading = useMemo(
    () => searchReviewLoading || selectedSearchReview?.id !== searchReviewId,
    [searchReviewLoading, selectedSearchReview?.id, searchReviewId]
  )

  const defaultValues: FormValues = useMemo(
    () => ({
      title: selectedSearchReview?.title || '',
      description: selectedSearchReview?.description || '',
      lastUpdate: selectedSearchReview?.lastModifiedDate,
    }),
    [selectedSearchReview]
  )
  const [
    selectedLiteratureReviewPanelType,
    setSelectedLiteratureReviewPanelType,
  ] = useState(
    (panelType || searchReviewPanelTypes.OVERVIEW) as SearchReviewPanelType
  )

  const [selectedLiteratureReviewTab, setSelectedLiteratureReviewTab] =
    useState<SearchReviewTabType>(
      (tab || searchReviewTabs.INFO) as SearchReviewTabType
    )

  const panelIndex = useCallback(() => {
    if (selectedLiteratureReviewPanelType === searchReviewPanelTypes.OVERVIEW) {
      switch (selectedLiteratureReviewTab) {
        case searchReviewTabs.INFO:
          return 0
        case searchReviewTabs.QUERIES:
          return 1
      }
    }
    return 0
  }, [selectedLiteratureReviewPanelType, selectedLiteratureReviewTab])

  useEffect(() => {
    form.setFieldsValue(defaultValues)
  }, [form, defaultValues])

  const handleSearchDelete = useCallback(() => {
    refreshSearchReviewDetails()
    notificationService.notificationSuccess({
      messageToDisplay:
        t('searchReviewDetailsPage.deleteSearchSuccessful') || '',
    })
  }, [refreshSearchReviewDetails])

  const handleDeleteSearchReview = () => {
    setConfirmDeleteModalOpen(true)
  }

  const handleConfirmDelete = () => {
    setDeleteLoading(true)
    deleteSearchReview({
      searchReviewId: selectedSearchReview?.id || '',
      navigate,
    })
      .then((response: SearchReview) => {
        notificationService.notificationSuccess({
          messageToDisplay:
            t('searchReviewDetailsPage.deleteLiteratureReviewSuccessful') || '',
        })
        navigate(routePaths.SEARCH_REVIEW_LIST)
        setDeleteLoading(false)
      })
      .catch((error: AxiosError | Error) => {
        console.error('axios fetch error', error)
        setDeleteLoading(false)
      })
      .finally(() => {
        setConfirmDeleteModalOpen(false)
      })
  }

  const handleSave = () => {
    setSaveLoading(true)
    const values = form.getFieldsValue()
    updateSearchReviewDetails({
      title: values.title,
      description: values.description || '',
    })
      .then(() => {
        notificationService.notificationSuccess({
          messageToDisplay:
            t('searchReviewDetailsPage.saveLiteratureReviewSuccessful') || '',
        })
      })
      .catch((error: AxiosError | Error) => {
        console.error('axios fetch error', error)
      })
      .finally(() => {
        setSaveLoading(false)
      })
  }

  useEffect(() => {
    setSelectedLiteratureReviewPanelType(
      (panelType || searchReviewPanelTypes.OVERVIEW) as SearchReviewPanelType
    )
    setSelectedLiteratureReviewTab(
      (tab || searchReviewTabs.INFO) as SearchReviewTabType
    )
  }, [panelType, tab])

  const handleLiteratureReviewExport = () => {
    exportSearchReviewCsv(selectedSearchReview?.id || '')
  }

  const panels = useMemo(
    () => [
      <SearchReviewDetailsOverviewInfoPanel />,
      <SearchReviewDetailsOverviewQueriesPanel
        handleSearchDelete={handleSearchDelete}
      />,
    ],
    [handleSearchDelete]
  )

  if (isSearchReviewLoading) {
    return <SectionLoadingState />
  }

  return (
    <DetailsPageLayout>
      <SubHeader>
        <BackButton url={routePaths.SEARCH_REVIEW_LIST} />
        <SelectWorkspacePanelType disabled={true} />
        <HeaderSeparator />
        <SearchReviewTabs />
        <Spacer />
        {tab === searchReviewTabs.INFO &&
          panelType === searchReviewPanelTypes.OVERVIEW && (
            <>
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleDeleteSearchReview}
                disabled={!isDeletable}
              >
                {!isMobile && t('Delete')}
              </Button>
              <ConfirmDeleteModal
                open={confirmDeleteModalOpen}
                onConfirm={handleConfirmDelete}
                onCancel={() => setConfirmDeleteModalOpen(false)}
                loading={deleteLoading}
              >
                {isMobile &&
                  t('searchReviewDetailsPage.confirmDeleteModalContent')}
              </ConfirmDeleteModal>
              <Button
                type="primary"
                style={{ marginLeft: '8px' }}
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={saveLoading}
                disabled={!isUpdatable}
              >
                {!isMobile && t('saveButton')}
              </Button>
            </>
          )}
        <PageContentDropdown onExport={handleLiteratureReviewExport} />
      </SubHeader>
      <Form
        form={form}
        layout="vertical"
        initialValues={defaultValues}
        onFinish={handleSave}
        requiredMark={false}
      >
        <ContentContainer>
          {
            panels[panelIndex()] // This is to render the correct panel based on the url
          }
        </ContentContainer>
      </Form>
    </DetailsPageLayout>
  )
}

export default SearchReviewDetailsPage
